import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const StoreTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [stores, setStores] = useState([]);
    const [newName, setNewName] = useState("");
    const [newAddress, setNewAddress] = useState("");
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [editAddress, setEditAddress] = useState("");

    const fetchStores = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/stores');
            setStores(data.stores);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load stores", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addHandler = async (e) => {
        e.preventDefault();
        if (!newName.trim() || !newAddress.trim()) return;
        setAdding(true);
        try {
            await axios.post('/api/v1/admin/store/new', { name: newName.trim(), address: newAddress.trim() });
            enqueueSnackbar("Store added", { variant: "success" });
            setNewName("");
            setNewAddress("");
            fetchStores();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to add store", { variant: "error" });
        } finally {
            setAdding(false);
        }
    };

    const startEdit = (store) => {
        setEditingId(store._id);
        setEditName(store.name);
        setEditAddress(store.address);
    };

    const saveEdit = async (id) => {
        if (!editName.trim() || !editAddress.trim()) return;
        try {
            await axios.put(`/api/v1/admin/store/${id}`, { name: editName.trim(), address: editAddress.trim() });
            enqueueSnackbar("Store updated", { variant: "success" });
            setEditingId(null);
            fetchStores();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to update store", { variant: "error" });
        }
    };

    const deleteHandler = async (id) => {
        if (!window.confirm("Delete this store? This can't be undone.")) return;
        try {
            await axios.delete(`/api/v1/admin/store/${id}`);
            enqueueSnackbar("Store deleted", { variant: "success" });
            setStores((prev) => prev.filter((s) => s._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete store", { variant: "error" });
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title="Admin: Stores | 1313 Mart" />

            <h1 className="text-lg font-medium uppercase mb-4">Stores</h1>

            <div className="bg-white rounded-xl shadow-lg w-full p-4 sm:p-6 flex flex-col gap-4">
                <form onSubmit={addHandler} className="flex flex-wrap gap-3 items-start">
                    <TextField label="Store Name" size="small" value={newName} onChange={(e) => setNewName(e.target.value)} sx={{ minWidth: 200 }} />
                    <TextField label="Address" size="small" value={newAddress} onChange={(e) => setNewAddress(e.target.value)} sx={{ minWidth: 300 }} />
                    <button type="submit" disabled={adding} className="bg-primary-orange px-5 py-2 text-white font-medium rounded shadow hover:shadow-lg disabled:opacity-60">
                        Add Store
                    </button>
                </form>

                <div className="flex flex-col divide-y">
                    {stores.map((store) => (
                        <div key={store._id} className="flex items-center justify-between py-3 gap-3">
                            {editingId === store._id ? (
                                <div className="flex gap-3 flex-1">
                                    <TextField size="small" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus sx={{ minWidth: 200 }} />
                                    <TextField size="small" value={editAddress} onChange={(e) => setEditAddress(e.target.value)} sx={{ minWidth: 300 }} />
                                </div>
                            ) : (
                                <div>
                                    <p className="text-sm font-medium text-gray-700">{store.name}</p>
                                    <p className="text-xs text-gray-400">{store.address}</p>
                                </div>
                            )}

                            <div className="flex items-center gap-1">
                                {editingId === store._id ? (
                                    <>
                                        <IconButton size="small" onClick={() => saveEdit(store._id)}>
                                            <CheckIcon sx={{ fontSize: "20px" }} color="success" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => setEditingId(null)}>
                                            <CloseIcon sx={{ fontSize: "20px" }} color="error" />
                                        </IconButton>
                                    </>
                                ) : (
                                    <>
                                        <IconButton size="small" onClick={() => startEdit(store)}>
                                            <EditIcon sx={{ fontSize: "18px" }} color="primary" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => deleteHandler(store._id)}>
                                            <DeleteIcon sx={{ fontSize: "18px" }} color="error" />
                                        </IconButton>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}

                    {stores.length === 0 && (
                        <p className="text-sm text-gray-400 py-3">No stores yet — add one above.</p>
                    )}
                </div>
            </div>
        </>
    );
};

export default StoreTable;